import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    computed,
    effect,
    forwardRef,
    inject,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormBuilder,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import {
    MatFormFieldAppearance,
    MatFormFieldModule,
} from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import {
    SelectFieldComponent,
} from '../select-field/select-field.component';
import { ListaStruttureStore } from '../struttura-ruoli-selector/strutture-selector.store';

/**
 * A struttura option as shown in the select.
 */
export interface StrutturaOption {
  id: number;
  name: string;
}

@UntilDestroy()
@Component({
   
  selector: 'siat-struttura-selector',
  templateUrl: './struttura-selector.component.html',
  styleUrls: ['./struttura-selector.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    SelectFieldComponent,
    TranslatePipe
],
  providers: [
    ListaStruttureStore,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StrutturaSelector),
      multi: true,
    },
  ],
})
export class StrutturaSelector implements OnInit, ControlValueAccessor {
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() id?: string;
  @Input() name?: string;
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() idStrutturaDefault?: number;
  @Output() strutturaSelected = new EventEmitter<StrutturaOption>();

  protected struttureStore = inject(ListaStruttureStore);
  private fb = inject(FormBuilder);

  formGroup!: FormGroup;

  itemsStrutture = computed(() => {
    return this.struttureStore.listaStruttureItemResponse();
  });

  private onChange = (_value: StrutturaOption | null) => {};
  private onTouched = () => {};

  constructor() {
    effect(() => {
      const strutture = this.itemsStrutture();

      if (this.idStrutturaDefault && strutture && strutture.length > 0) {
        const strutturaSelezionata = strutture.find(item => item.id === this.idStrutturaDefault);

        if (strutturaSelezionata && !this.formGroup?.get('struttura')?.value) {
          this.formGroup.patchValue({ struttura: strutturaSelezionata });
          this.formGroup.get('struttura').disable();
        }
      }
    });
  }

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      struttura: [{ value: null, disabled: !!this.idStrutturaDefault }]
    });

    this.struttureStore.fetchGetAllStrutture('');

    // Listen for struttura changes
    this.formGroup.get('struttura')?.valueChanges.subscribe(struttura => {
      this.onChange(struttura);
      this.onTouched();
      if (struttura) {
        this.strutturaSelected.emit(struttura);
      }
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: StrutturaOption | null): void {
    this.formGroup.patchValue({ struttura: value });
  }

  registerOnChange(fn: (value: StrutturaOption | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }
}